import {
  Group,
  LoadingOverlay,
  Space,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { useWindowScroll } from "@mantine/hooks";
import {
  IconCalendarEvent,
  IconChecklist,
  IconSettings,
  IconTable,
  IconTrophy,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import pluralize from "pluralize";
import { ParsedUrlQuery } from "querystring";
import { useEffect, useState } from "react";
import {
  MAX_PAGE_SIZE,
  getBoard,
  getEvent,
  getEventScreenshotUrl,
  listEvents,
} from "../../../../../../common/api";
import {
  useBoard,
  useIsBoardReferee,
} from "../../../../../../common/boards/board.hooks";
import { Board } from "../../../../../../common/boards/board.schema";
import { Event, EventType } from "../../../../../../common/events/event.schema";
import { Swords } from "../../../../../../common/icons/swords";
import { Member } from "../../../../../../common/members/member.schema";
import { useAppStore } from "../../../../../../common/stores/app.store";
import { getSlug } from "../../../../../../common/util";
import BoardModalProvider from "../../../../../../components/common/BoardModalProvider";
import Hider from "../../../../../../components/common/Hider";
import Loading from "../../../../../../components/common/Loading";
import PageTitle from "../../../../../../components/common/PageTitle";
import RecordAvatar from "../../../../../../components/common/RecordAvatar";
import Eliminations from "../../../../../../components/events/Eliminations";
import Pools from "../../../../../../components/events/Pools";
import Settings from "../../../../../../components/events/Settings";
import Summary from "../../../../../../components/events/Summary";
import {
  getHasEliminations,
  getHasPools,
  getHasTeams,
} from "../../../../../../components/events/util";

interface Props {
  board?: Board;
  event?: Event;
  eventShortLink: string;
}
/* eslint-disable unused-imports/no-unused-vars */
enum TabState {
  SUMMARY = "SUMMARY",
  POOLS = "POOLS",
  ELIMINATIONS = "ELIMINATIONS",
  SETTINGS = "SETTINGS",
}
export const REFETCH_INTERVAL = 10000;
/* eslint-enable unused-imports/no-unused-vars */
const EventScreen = ({ board: initBoard, event: initEvent }: Props) => {
  // const { t } = useTranslation();
  const savingEventMatch = useAppStore((s) => s.savingEventMatch);
  const setSavingEventMatch = useAppStore((s) => s.setSavingEventMatch);
  const [complete, setComplete] = useState(initEvent?.complete);
  const [started, setStarted] = useState(initEvent?.started);
  const [poolsComplete, setPoolsComplete] = useState(initEvent?.poolsComplete);
  const router = useRouter();
  const { board } = useBoard(initBoard);
  const { data: event } = useQuery(
    ["event", initEvent?.shortLink],
    () => initEvent && getEvent(initEvent?.shortLink),
    {
      initialData: initEvent,
      refetchInterval: complete ? REFETCH_INTERVAL : false,
      onSuccess: () => {
        setSavingEventMatch(false);
      },
    }
  );
  // console.log("isLoading :", isLoading);
  // console.log("status :", status);
  const theme = useMantineTheme();
  const { t } = useTranslation();
  // const admin = useIsBoardAdmin();
  const [activeTab, setActiveTab] = useState<TabState>(TabState.SUMMARY);
  const isRef = useIsBoardReferee();
  const colorScheme = useComputedColorScheme();
  const hasPools = event && getHasPools(event);
  const hasTeams = event && getHasTeams(event);
  const hasEliminations = event && getHasEliminations(event);
  useEffect(() => {
    if (event?.complete && !complete) {
      setActiveTab(TabState.SUMMARY);
    }
    setComplete(event?.complete);
  }, [complete, event?.complete]);
  useEffect(() => {
    if (event?.started && !started) {
      if (hasPools) {
        setActiveTab(TabState.POOLS);
      } else if (hasEliminations) {
        setActiveTab(TabState.ELIMINATIONS);
      }
    }
    setStarted(event?.started);
  }, [hasEliminations, hasPools, started, event?.started]);
  useEffect(() => {
    if (event?.poolsComplete && !poolsComplete) {
      if (hasEliminations && !hasTeams) {
        setActiveTab(TabState.ELIMINATIONS);
      }
      if (hasEliminations && hasTeams) {
        setActiveTab(TabState.SUMMARY);
      }
      if (!hasEliminations) {
        setActiveTab(TabState.SUMMARY);
      }
    }
    setPoolsComplete(event?.poolsComplete);
  }, [hasEliminations, hasTeams, poolsComplete, event?.poolsComplete]);
  const goCurrent = () => {
    if (hasPools && !poolsComplete) {
      setActiveTab(TabState.POOLS);
    }
    if (!hasPools || poolsComplete) {
      setActiveTab(TabState.ELIMINATIONS);
    }
  };
  const [scroll, scrollTo] = useWindowScroll();
  useEffect(() => {
    if (scroll.y > 0) scrollTo({ y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, scrollTo]);
  const getTitle = (board?: Board, event?: Event) => {
    if (!board || !event) return <></>;
    const title = `${t("scoreboarder")} | ${board.name} | Events | ${
      event.name
    }`;
    let description = `Live results for ${event.name} by Scoreboarder. Easy to use leaderboards and events.`;
    if (event.complete) {
      const members = event.members as Member[];
      if (event.teams.length > 0) {
        const winner = (event.teams as Member[]).find(
          (m) => m._id === event.results[0]
        );
        if (winner) {
          description = `Results for ${event.name} of ${board.name}. `;
          description += `Won by ${winner.name}: `;
          const teamMemberIds = winner.members as string[];
          for (let index = 0; index < teamMemberIds.length; index++) {
            const id = teamMemberIds[index];
            const teamMember = members.find((tm) => tm._id === id);
            if (index === teamMemberIds.length - 1) {
              description += `and ${teamMember?.name}.`;
            } else {
              description += `${teamMember?.name}, `;
            }
          }
        }
      } else {
        const winner = members.find((m) => m._id === event.results[0]);
        description = `Results for ${event.name} of ${board.name}. `;
        if (winner) {
          const name = `${winner.name} ${winner?.lastName}`.trim();
          description += `Won by ${name}.`;
        }
        description = description.trim();
      }
    }
    const image = getEventScreenshotUrl(event._id);
    // console.log("image :", image);
    return <PageTitle title={title} image={image} description={description} />;
  };
  // console.log("event :", event);
  if (router.isFallback || !board || !event) {
    return (
      <>
        {getTitle(initBoard, initEvent)}
        <Loading />
      </>
    );
  }
  const eliminationsEnabled =
    event.eliminationSeeds.length > 0 &&
    event.started &&
    (!hasPools || event.poolsComplete);
  const isTournament = event.eventType === EventType.TOURNAMENT;
  const showTabs = isTournament || isRef;
  return (
    <BoardModalProvider>
      {getTitle(board || initBoard, event || initEvent)}
      <Stack style={{ position: "relative" }}>
        <LoadingOverlay
          visible={savingEventMatch === true}
          overlayProps={{
            blur: 2,
            c:
              colorScheme === "dark"
                ? theme.colors?.dark[8]
                : theme.colors?.light && theme.colors?.light[0],
          }}
        />
        {getTitle(board, event)}
        <Group justify="space-between">
          <Group>
            <RecordAvatar record={event} size="sm" />
            <Title order={2} fz="md">
              {event?.name}
            </Title>
          </Group>
          {isTournament && (
            <ThemeIcon variant="light" size="sm">
              <IconTrophy />
            </ThemeIcon>
          )}
          {!isTournament && (
            <ThemeIcon variant="light" size="sm">
              <IconCalendarEvent />
            </ThemeIcon>
          )}
        </Group>
        {showTabs && (
          <Tabs
            value={activeTab}
            keepMounted={false}
            onChange={(state) => setActiveTab(state as TabState)}
          >
            <Tabs.List grow>
              <Tabs.Tab
                leftSection={
                  <ThemeIcon size="xs" variant="light">
                    <IconChecklist />
                  </ThemeIcon>
                }
                value={TabState.SUMMARY}
              >
                <Hider smallerThan="xs">
                  <Text>{t("Summary")}</Text>
                </Hider>
              </Tabs.Tab>
              {hasPools && (
                <Tabs.Tab
                  disabled={!event.started}
                  leftSection={
                    <ThemeIcon size="sm" variant="light">
                      <IconTable />
                    </ThemeIcon>
                  }
                  value={TabState.POOLS}
                >
                  <Hider smallerThan="xs">
                    <Text>{pluralize("Pool", event.pools.length)}</Text>
                  </Hider>
                </Tabs.Tab>
              )}
              {hasEliminations && (
                <Tabs.Tab
                  disabled={!eliminationsEnabled}
                  leftSection={
                    <ThemeIcon size="sm" variant="light">
                      <Swords />
                    </ThemeIcon>
                  }
                  value={TabState.ELIMINATIONS}
                >
                  <Hider smallerThan="xs">
                    <Text>{t("Eliminations")}</Text>
                  </Hider>
                </Tabs.Tab>
              )}
              {isRef && (
                <Tabs.Tab
                  leftSection={
                    <ThemeIcon size="sm" variant="light">
                      <IconSettings />
                    </ThemeIcon>
                  }
                  value={TabState.SETTINGS}
                >
                  <Hider smallerThan="xs">
                    <Text>{t("settings")}</Text>
                  </Hider>
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel value={TabState.SUMMARY}>
              <Space h={10} />
              <Summary
                board={board}
                event={event}
                onSelectViewCurrent={goCurrent}
              />
            </Tabs.Panel>
            <Tabs.Panel value={TabState.POOLS}>
              <Space h={10} />
              <Pools event={event} />
            </Tabs.Panel>
            <Tabs.Panel value={TabState.ELIMINATIONS}>
              <Space h={10} />
              <Eliminations event={event} />
            </Tabs.Panel>
            <Tabs.Panel value={TabState.SETTINGS}>
              <Space h={10} />
              <Settings event={event} />
            </Tabs.Panel>
          </Tabs>
        )}
        {!showTabs && (
          <Summary
            board={board}
            event={event}
            onSelectViewCurrent={goCurrent}
          />
        )}
      </Stack>
    </BoardModalProvider>
  );
};
export default EventScreen;

interface IParams extends ParsedUrlQuery {
  boardShortLink: string;
  eventShortLink: string;
}
export const getStaticProps: GetStaticProps = async (context) => {
  const { boardShortLink, eventShortLink } = context.params as IParams;
  // console.log("memberShortLink :", memberShortLink);
  // console.log("boardShortLink :", boardShortLink);
  let board, event;
  try {
    board = await getBoard(boardShortLink);
    // console.log("board :", board);
    event = await getEvent(eventShortLink);
  } catch {}
  if (!board || !event) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  // console.log("members :", members);
  return {
    revalidate: 60,
    props: {
      ...(await serverSideTranslations(context.locale || "en")),
      board,
      event,
      eventShortLink,
    },
  };
};

export async function getStaticPaths() {
  return { paths: [], fallback: true };
  const res = await listEvents({
    limit: MAX_PAGE_SIZE,
    populate: ["board"],
    sort: "-updatedAt",
  });
  // console.log("boardRes :", boardRes);
  let paths = res.records.map((event) => {
    // console.log("event :", event);
    const board = event.board as Board;
    return {
      params: {
        eventShortLink: event.shortLink,
        eventSlug: getSlug(event),
        boardShortLink: board.shortLink,
        boardSlug: getSlug(board),
      },
    };
  });
  paths = paths.filter(
    (p) =>
      p.params.boardShortLink &&
      p.params.boardSlug &&
      p.params.eventShortLink &&
      p.params.eventSlug
  );
  // console.log("paths :", paths);
  return {
    paths,
    fallback: true,
  };
}
